import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErr } from "@/lib/api";

// Manual editor for uploaded/forked artifacts (2026-07-13). Content is edited as plain text in
// textareas — never rendered as HTML (stored-XSS invariant). Saving re-enters review at whatever
// boundary the artifact sits on: live gallery listings re-scan immediately; org artifacts keep
// serving the approved version until re-submitted.
export default function EditSkill() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const [skill, setSkill] = useState(null);
  const [skillMd, setSkillMd] = useState("");
  const [files, setFiles] = useState([]);
  const [changelog, setChangelog] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/skills/${skillId}`)
      .then(({ data }) => {
        if (!data.is_creator || data.skill_md_locked) {
          toast.error("Only the creator can edit this");
          navigate("/my-skills");
          return;
        }
        setSkill(data);
        setSkillMd(data.skill_md || "");
        setFiles((data.files || []).map((f) => ({ filename: f.filename || "", content: f.content || "" })));
      })
      .catch(() => {
        toast.error("Skill not found");
        navigate("/my-skills");
      });
  }, [skillId, navigate]);

  const setFile = (i, patch) => setFiles((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/skills/${skillId}/content`, { skill_md: skillMd, files });
      toast[data.review_status === "rejected" ? "error" : "success"](data.message);
      return data;
    } catch (e) {
      toast.error(apiErr(e, "Couldn't save"));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveAndSubmit = async () => {
    if (!(await save())) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/skills/${skillId}/submit`, { changelog: changelog.trim() || "Edited" });
      toast.success(data.status === "published" ? "Live in your org's catalog — the plugin will sync it." : "Submitted — awaiting your org admin's approval.");
      navigate("/my-skills");
    } catch (e) {
      toast.error(apiErr(e, "Couldn't submit"));
    } finally {
      setBusy(false);
    }
  };

  if (!skill) {
    return (
      <AppShell>
        <div className="flex justify-center py-24 text-[color:var(--rb-text-faint)]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </AppShell>
    );
  }

  const isSkillKind = !skill.kind || skill.kind === "skill";

  return (
    <AppShell>
      <button onClick={() => navigate("/my-skills")} className="mb-4 flex items-center gap-1.5 text-sm text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
        <ArrowLeft className="h-4 w-4" /> My Artifacts
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="rb-mono text-2xl font-semibold text-[color:var(--rb-text)]">{skill.slug}</h1>
          <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
            v{skill.content_version || 1} — changed content goes back through review before it reaches anyone.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} data-testid="edit-save-button"
            className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save</>}
          </Button>
          {skill.org_id && (
            <Button onClick={saveAndSubmit} disabled={busy} variant="outline" data-testid="edit-submit-button"
              className="rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:bg-[color:var(--rb-surface)]">
              <Send className="mr-2 h-4 w-4" /> Save & submit for approval
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {/* The changelog lives where the change happens (2026-07-14) — it rides the submit
            and shows up in the org version history + the admin's queue. */}
        {skill.org_id && (
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">What changed, and why? (shown to your admin and in version history)</label>
            <Input value={changelog} onChange={(e) => setChangelog(e.target.value)} data-testid="edit-changelog-input"
              placeholder="e.g. Tightened the deploy checklist after Tuesday's incident"
              className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]" />
          </div>
        )}
        <div>
          <label className="text-xs text-[color:var(--rb-text-faint)]">{isSkillKind ? "SKILL.md" : "Content"}</label>
          <Textarea value={skillMd} onChange={(e) => setSkillMd(e.target.value)} data-testid="edit-skill-md"
            className="rb-mono mt-1 min-h-[50vh] border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]" />
        </div>

        {isSkillKind && (
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-[color:var(--rb-text-faint)]">Helper files</label>
              <Button size="sm" variant="outline" onClick={() => setFiles((fs) => [...fs, { filename: "", content: "" }])}
                data-testid="edit-add-file" className="border-[color:var(--rb-border)] text-[color:var(--rb-text)]">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add file
              </Button>
            </div>
            {files.map((f, i) => (
              <div key={i} className="mt-3 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-3">
                <div className="flex items-center gap-2">
                  <Input value={f.filename} onChange={(e) => setFile(i, { filename: e.target.value })} placeholder="scripts/helper.py"
                    className="rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text)]" />
                  <Button size="sm" variant="ghost" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                    className="shrink-0 text-[color:var(--rb-text-faint)] hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea value={f.content} onChange={(e) => setFile(i, { content: e.target.value })}
                  className="rb-mono mt-2 min-h-[16vh] border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]" />
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
